import { useEffect, useRef, useState } from "react";
import {
  Button,
  TextField,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { useSelector } from "react-redux";

import Spinner from "./../Component/Spinner/Spinner";
import api from "./../BrandOnBoarding/api";

const CREATE_NEW_GROUP = "__create_new__";

export default function AddCompanyUserForm({
  customerId,
  onSubmit,
  onCancel,
  type,
}) {
  const { isAddingUser, users } = useSelector((state) => state.user);
  const companyInputRef = useRef();
  const companyDominRef = useRef();
  const companySelectRef = useRef();
  const emailInputRef = useRef();

  const [customerGroups, setCustomerGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [groupError, setGroupError] = useState("");
  const [industry, setIndustry] = useState("");

  useEffect(() => {
    if (type !== "company") return;
    setLoadingGroups(true);
    api
      .get("/customer-groups")
      .then((res) => setCustomerGroups(res.data?.data || []))
      .catch(() => {})
      .finally(() => setLoadingGroups(false));
  }, [type]);

  function handleGroupChange(e) {
    const val = e.target.value;
    setSelectedGroupId(val);
    setIsCreatingGroup(val === CREATE_NEW_GROUP);
    setGroupError("");
    if (val !== CREATE_NEW_GROUP) setNewGroupName("");
  }

  async function handleFormSubmit(e) {
    e.preventDefault();

    if (type !== "user" && !selectedGroupId) {
      setGroupError("Please select a group");
      return;
    }

    let company = companyInputRef?.current?.value;
    let domain = companyDominRef?.current?.value;
    if (type === "user") company = companySelectRef?.current?.value;

    let resolvedGroupId = selectedGroupId;

    if (type !== "user" && selectedGroupId === CREATE_NEW_GROUP) {
      if (!newGroupName.trim()) {
        setGroupError("Please enter a group name");
        return;
      }
      try {
        const res = await api.post("/customer-groups", { name: newGroupName.trim() });
        resolvedGroupId = res.data?.data?.id;
        if (!resolvedGroupId) throw new Error("Invalid response");
      } catch {
        setGroupError("Failed to create group. Please try again.");
        return;
      }
    }

    onSubmit(
      company,
      domain,
      emailInputRef?.current?.value,
      resolvedGroupId || null,
      industry.trim() || null
    );
  }

  return (
    <form className="d-flex flex-column mt-2" onSubmit={handleFormSubmit}>
      {type === "company" && (
        <>
          {/* Group */}
          <FormControl fullWidth size="small" className="mb-4" error={!!groupError}>
            <InputLabel id="group-label">Group *</InputLabel>
            <Select
              labelId="group-label"
              label="Group *"
              value={selectedGroupId}
              onChange={handleGroupChange}
              disabled={loadingGroups}
              required
              endAdornment={
                loadingGroups ? (
                  <CircularProgress size={16} sx={{ mr: 2, flexShrink: 0 }} />
                ) : null
              }
            >
              <MenuItem
                value={CREATE_NEW_GROUP}
                sx={{ fontStyle: "italic", color: "primary.main" }}
              >
                + Create New Group
              </MenuItem>
              {customerGroups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
            {groupError && <FormHelperText>{groupError}</FormHelperText>}
          </FormControl>

          {/* New group name — shown only when "Create New Group" is selected */}
          {isCreatingGroup && (
            <TextField
              fullWidth
              size="small"
              className="mb-4"
              label="New Group Name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="e.g. Tesla Group"
              required
            />
          )}
        </>
      )}

      {type === "company" && (
        <TextField
          inputRef={companyInputRef}
          id="outlined-basic"
          size="small"
          label="Company Name"
          className="mb-4"
          type="text"
          variant="outlined"
          required
        />
      )}
      {type === "user" && !customerId && (
        <FormControl fullWidth size="small" className="mb-4">
          <InputLabel id="company">Company</InputLabel>
          <Select
            labelId="company"
            label="Company"
            inputRef={companySelectRef}
            required
          >
            {users
              .filter((user) => user.user_role.role_id === 2)
              .map((user) => (
                <MenuItem value={user.customer.id} key={user.customer.id}>
                  {user.customer.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      )}
      {type === "company" && (
        <TextField
          inputRef={companyDominRef}
          id="outlined-basic"
          size="small"
          className="mb-4"
          label="Company Domain URL"
          variant="outlined"
          type="url"
          required
        />
      )}
      <TextField
        inputRef={emailInputRef}
        id="outlined-basic"
        size="small"
        label={type === "user" ? "User E-Mail" : "Admin E-Mail"}
        variant="outlined"
        type="email"
        required
      />

      {type === "company" && (
        <TextField
          fullWidth
          size="small"
          className="mt-3"
          label="Industry"
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          placeholder="e.g. Healthcare, Automobile, General"
        />
      )}

      <div className="d-flex flex-row mt-4 justify-content-end">
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <span style={{ marginRight: 10 }}></span>
        <Button
          disabled={isAddingUser}
          type="submit"
          variant="contained"
          className="ml-2"
        >
          {isAddingUser ? (
            <Spinner
              style={{ display: "flex", margin: 0, padding: 0 }}
              size={16}
            />
          ) : (
            "Save"
          )}
        </Button>
      </div>
    </form>
  );
}
